'use client';
import React, { useState } from 'react';
import { Search, Bell, ChevronDown, Menu, LogOut, Loader2 } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface TopbarProps {
  onMobileMenuToggle?: () => void;
}

const notifications = [
  { id: 'notif-1', text: '7 memberships expiring this week', type: 'warning', time: '2m ago' },
  { id: 'notif-2', text: 'Arjun Mehta has an overdue payment of ₹2,400', type: 'danger', time: '15m ago' },
  { id: 'notif-3', text: 'Court B2 booking confirmed for 6:00 PM', type: 'success', time: '1h ago' },
  { id: 'notif-4', text: 'New member Priya Nair registered', type: 'info', time: '2h ago' },
];

export default function Topbar({ onMobileMenuToggle }: TopbarProps) {
  const [showNotifs, setShowNotifs] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const { staffProfile, isOwner, assignedRoles } = usePermissions();
  const { signOut } = useAuth();
  const router = useRouter();

  const typeColor: Record<string, string> = {
    warning: 'text-warning', danger: 'text-danger', success: 'text-success', info: 'text-info',
  };
  const typeDot: Record<string, string> = {
    warning: 'bg-warning', danger: 'bg-danger', success: 'bg-success', info: 'bg-info',
  };

  const getInitials = (name: string) =>
    name?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2) || 'AS';

  const getRoleLabel = () => {
    if (isOwner) return 'Arena Owner';
    if (assignedRoles.length > 0) return assignedRoles[0].name;
    return 'Staff';
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    setShowUser(false);
    try {
      await signOut();
      router.push('/sign-up-login');
    } catch {
      setSigningOut(false);
    }
  };

  return (
    <header className="h-16 bg-card border-b border-border flex items-center px-4 gap-4 sticky top-0 z-30 flex-shrink-0">
      {/* Mobile menu */}
      <button className="lg:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground" onClick={onMobileMenuToggle}>
        <Menu size={20} />
      </button>

      {/* Mobile logo */}
      <div className="lg:hidden flex items-center gap-2">
        <AppLogo size={28} />
        <span className="font-bold text-[14px] text-foreground">ArenaSync</span>
      </div>

      {/* Search */}
      <div className="hidden md:flex flex-1 max-w-sm relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search members, bookings..."
          className="w-full pl-9 pr-4 py-2 text-[13px] bg-muted rounded-lg border border-transparent focus:border-ring focus:bg-card focus:outline-none transition-all"
        />
      </div>

      <div className="flex-1" />

      {/* Today's date */}
      <div className="hidden lg:block text-[12px] text-muted-foreground font-medium">
        <span>Tue, 11 Aug 2026</span>
      </div>

      {/* Notifications */}
      <div className="relative">
        <button
          onClick={() => { setShowNotifs(!showNotifs); setShowUser(false); }}
          className="relative p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        >
          <Bell size={18} />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-danger rounded-full" />
        </button>
        {showNotifs && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-xl shadow-lg z-50 fade-in">
            <div className="p-3 border-b border-border">
              <p className="text-[13px] font-semibold text-foreground">Notifications</p>
              <p className="text-[11px] text-muted-foreground">{notifications.length} unread</p>
            </div>
            <ul>
              {notifications.map((n) => (
                <li key={n.id} className="px-3 py-2.5 hover:bg-muted cursor-pointer transition-colors border-b border-border last:border-0">
                  <div className="flex items-start gap-2">
                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${typeDot[n.type]}`} />
                    <div>
                      <p className={`text-[12px] font-medium ${typeColor[n.type]}`}>{n.text}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{n.time}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="p-2">
              <button className="w-full text-[12px] text-primary font-medium hover:bg-primary/5 rounded-lg py-1.5 transition-colors">
                View all notifications
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User menu */}
      <div className="relative">
        <button
          onClick={() => { setShowUser(!showUser); setShowNotifs(false); }}
          className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-muted transition-colors"
        >
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
            <span className="text-[11px] font-bold text-primary">
              {getInitials(staffProfile?.full_name || 'AS')}
            </span>
          </div>
          <div className="hidden md:block text-left">
            <p className="text-[12px] font-semibold text-foreground leading-tight">
              {staffProfile?.full_name || 'Arena Staff'}
            </p>
            <p className="text-[10px] text-muted-foreground">{getRoleLabel()}</p>
          </div>
          <ChevronDown size={14} className="text-muted-foreground hidden md:block" />
        </button>
        {showUser && (
          <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-xl shadow-lg z-50 fade-in py-1">
            <div className="px-3 py-2 border-b border-border">
              <p className="text-[12px] font-semibold text-foreground">{staffProfile?.full_name || 'Arena Staff'}</p>
              <p className="text-[11px] text-muted-foreground">{staffProfile?.email || ''}</p>
            </div>
            {['Profile Settings', 'Help & Support'].map((item) => (
              <button key={`user-menu-${item}`} className="w-full text-left px-3 py-2 text-[13px] text-secondary-foreground hover:bg-muted hover:text-foreground transition-colors">
                {item}
              </button>
            ))}
            <div className="border-t border-border mt-1 pt-1">
              <button
                onClick={handleSignOut}
                disabled={signingOut}
                className="w-full text-left px-3 py-2 text-[13px] text-danger hover:bg-danger-bg transition-colors flex items-center gap-2"
              >
                {signingOut ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />}
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}